// UploadAckDialog — first-upload-per-venue acknowledgment of the current
// Anthropic data-handling posture. Per plan KD2 layer 3, the operator
// confirms the venue's tour packet doesn't contain content barred from
// third-party AI processing before the upload proceeds. The ack is
// folded into the upload POST request body (no separate PATCH), so the
// dialog returns the values to the parent which then issues the upload.
//
// Posture strings shown are humanized — never expose the raw enum
// identifiers (verified_default_api, verified_zdr) directly to the UI.

import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { DataHandlingPosture } from "@/lib/types";

interface UploadAckDialogProps {
  open: boolean;
  posture: DataHandlingPosture;
  filename: string;
  onCancel: () => void;
  onConfirm: (ack: { acknowledged_posture: string; acknowledged_at: string }) => void;
}

const POSTURE_COPY: Record<
  DataHandlingPosture["posture"],
  { headline: string; detail: string }
> = {
  verified_default_api: {
    headline: "Anthropic's standard API",
    detail:
      "Overdrive uses Anthropic's standard API for AI extraction: " +
      "7-day backend log retention, zero training-data use, " +
      "Messages API endpoints only. Confirm this document doesn't " +
      "contain content your client's NDA would bar from third-party " +
      "AI processing.",
  },
  verified_zdr: {
    headline: "Zero Data Retention",
    detail:
      "Overdrive has a signed Zero Data Retention agreement with " +
      "Anthropic: data is not stored at rest after the API response " +
      "is returned (Trust & Safety exception applies). Confirm this " +
      "document is appropriate to process under this posture.",
  },
  // Should never reach this dialog; the U12 banner blocks the upload
  // button when posture is unverified. Defensive copy in case the
  // banner check is bypassed somehow.
  unverified: {
    headline: "Data-handling posture not verified",
    detail:
      "Uploads are disabled until an admin records the Anthropic " +
      "data-handling posture. Contact your administrator.",
  },
};

export function UploadAckDialog({
  open,
  posture,
  filename,
  onCancel,
  onConfirm,
}: UploadAckDialogProps) {
  const [confirmed, setConfirmed] = useState(false);
  const copy = POSTURE_COPY[posture.posture];

  const handleConfirm = () => {
    if (!confirmed || posture.posture === "unverified") return;
    onConfirm({
      acknowledged_posture: posture.posture,
      acknowledged_at: new Date().toISOString(),
    });
    setConfirmed(false);
  };

  const handleCancel = () => {
    setConfirmed(false);
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm AI processing — {copy.headline}</DialogTitle>
          <DialogDescription className="pt-2 text-sm">
            About to upload <strong>{filename}</strong> for AI extraction.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm text-foreground">
          <p>{copy.detail}</p>
          <p>
            <a
              href={posture.anthropic_terms_reference_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              View Anthropic's data-handling documentation →
            </a>
          </p>

          {posture.posture !== "unverified" && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-muted/30 p-3">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1"
              />
              <span>
                I confirm this document doesn't contain content barred from
                third-party AI processing under this venue's contractual
                obligations.
              </span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!confirmed || posture.posture === "unverified"}
          >
            Confirm and upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
